import { FunctionComponent, ReactNode, useMemo } from 'react';
import { DefaultTheme, ThemeProvider as StyledThemeProvider } from 'styled-components';

import {
  AvatarThemeContext,
  BackdropTheme,
  ButtonTheme,
  CheckboxTheme,
  DataGridThemeContext,
  DialogTheme,
  DrawerTheme,
  DropdownThemeContext,
  IconButtonThemeContext,
  MenuThemeContext,
  PaginationThemeContext,
  PopupThemeContext,
  RadioTheme,
  SnackbarAlertsThemeContext,
  SnackbarThemeContext,
  SwitchTheme,
  TabTheme,
  TagThemeContext,
  TextInputThemeContext,
  ThemeContext,
  TooltipThemeContext,
} from '@sternum/ui';

import { SBOMDarkAvatarTheme, SBOMLightAvatarTheme } from '../avatar';
import { SBOMDarkBackdropTheme } from '../backdrop/Dark.theme';
import { SBOMLightBackdropTheme } from '../backdrop/Light.theme';
import { SBOMDarkButtonTheme } from '../button/Dark.theme';
import { SBOMLightButtonTheme } from '../button/Light.theme';
import { SBOMDarkCheckboxTheme } from '../checkbox/Dark.theme';
import { SBOMLightCheckboxTheme } from '../checkbox/Light.theme';
import { SBOMDarkDatagridTheme, SBOMLightDatagridTheme } from '../datagrid';
import { SBOMDarkDialogTheme, SBOMLightDialogTheme } from '../dialog';
import { SBOMDarkDrawerTheme, SBOMLightDrawerTheme } from '../drawer';
import { SBOMDarkDropdownTheme, SBOMLightDropdownTheme } from '../dropdown';
import { SBOMDarkIconButtonTheme, SBOMLightIconButtonTheme } from '../icon-button';
import { SBOMDarkMenuTheme, SBOMLightMenuTheme } from '../menu';
import { SBOMDarkPaginationTheme, SBOMLightPaginationTheme } from '../pagination';
import { SBOMDarkPopupTheme, SBOMLightPopupTheme } from '../popup';
import { SBOMDarkRadioTheme, SBOMLightRadioTheme } from '../radio';
import { SBOMDarkSnackbarTheme, SBOMLightSnackbarTheme } from '../snackbar';
import { SBOMDarkSnackbarAlertsTheme, SBOMLightSnackbarAlertsTheme } from '../snackbarAlerts';
import { SBOMDarkSwitchTheme, SBOMLightSwitchTheme } from '../switch';
import { SBOMDarkTabsTheme, SBOMLightTabsTheme } from '../tabs';
import { SBOMDarkTagTheme, SBOMLightTagTheme } from '../tag';
import { SBOMDarkTextInputTheme, SBOMLightTextInputTheme } from '../text-input';
import { SBOMDarkTooltipTheme, SBOMLightTooltipTheme } from '../tooltip';

import { darkTheme } from './Dark.theme';
import { lightTheme } from './Light.theme';
import { ThemeMode, ThemeModeContext } from './ThemeMode.context';

export interface ThemeProviderProps {
  mode?: ThemeMode;
  children?: ReactNode;
}

interface Provider {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  provider: FunctionComponent<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}

export function ThemeProvider({ mode = 'Dark', children }: ThemeProviderProps) {
  const globalTheme = mode === 'Dark' ? darkTheme : lightTheme;

  const providers = useMemo<Provider[]>(
    () => [
      {
        provider: AvatarThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkAvatarTheme(darkTheme) : SBOMLightAvatarTheme(lightTheme),
      },
      {
        provider: BackdropTheme.BackdropThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkBackdropTheme(darkTheme) : SBOMLightBackdropTheme(lightTheme),
      },
      {
        provider: DrawerTheme.DrawerThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkDrawerTheme(darkTheme) : SBOMLightDrawerTheme(lightTheme),
      },
      {
        provider: DropdownThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkDropdownTheme(darkTheme) : SBOMLightDropdownTheme(lightTheme),
      },
      {
        provider: DialogTheme.DialogThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkDialogTheme(darkTheme) : SBOMLightDialogTheme(lightTheme),
      },
      {
        provider: CheckboxTheme.CheckboxThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkCheckboxTheme(darkTheme) : SBOMLightCheckboxTheme(darkTheme),
      },
      {
        provider: RadioTheme.RadioThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkRadioTheme(darkTheme) : SBOMLightRadioTheme(darkTheme),
      },
      {
        provider: SwitchTheme.SwitchThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkSwitchTheme(darkTheme) : SBOMLightSwitchTheme(darkTheme),
      },
      {
        provider: TextInputThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkTextInputTheme(darkTheme) : SBOMLightTextInputTheme(lightTheme),
      },
      {
        provider: ButtonTheme.ButtonThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkButtonTheme(darkTheme) : SBOMLightButtonTheme(darkTheme),
      },
      {
        provider: IconButtonThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkIconButtonTheme(darkTheme) : SBOMLightIconButtonTheme(darkTheme),
      },
      {
        provider: SnackbarAlertsThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkSnackbarAlertsTheme(darkTheme) : SBOMLightSnackbarAlertsTheme(darkTheme),
      },
      {
        provider: SnackbarThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkSnackbarTheme(darkTheme) : SBOMLightSnackbarTheme(lightTheme),
      },
      {
        provider: TooltipThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkTooltipTheme(darkTheme) : SBOMLightTooltipTheme(darkTheme),
      },
      {
        provider: DataGridThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkDatagridTheme(darkTheme) : SBOMLightDatagridTheme(darkTheme),
      },
      {
        provider: MenuThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkMenuTheme(darkTheme) : SBOMLightMenuTheme(lightTheme),
      },
      {
        provider: PaginationThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkPaginationTheme(darkTheme) : SBOMLightPaginationTheme(darkTheme),
      },
      {
        provider: TagThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkTagTheme(darkTheme) : SBOMLightTagTheme(lightTheme),
      },
      {
        provider: TabTheme.TabThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkTabsTheme(darkTheme) : SBOMLightTabsTheme(lightTheme),
      },
      {
        provider: PopupThemeContext.Provider,
        value: mode === 'Dark' ? SBOMDarkPopupTheme(darkTheme) : SBOMLightPopupTheme(darkTheme),
      },
      {
        provider: ThemeContext.Provider,
        value: globalTheme,
      },
      {
        provider: ThemeModeContext.Provider,
        value: mode,
      },
    ],
    [mode, globalTheme],
  );

  const combinedProviders = useMemo<JSX.Element>(() => {
    let tmpChildren = children;

    for (let i = 0; i < providers.length; ++i) {
      const Provider = providers[i].provider;
      const value = providers[i].value;

      tmpChildren = <Provider value={value}>{tmpChildren}</Provider>;
    }

    return tmpChildren as JSX.Element;
  }, [children, providers]);

  const defaultTheme = useMemo<DefaultTheme>(
    () => ({
      mainThemeMode: mode,
      textColor: globalTheme.textColor,

      layout: {
        headerHeight: 48,
        mainFullMenuWidth: 240,
        mainCollapsedMenuWidth: 66,
        dashboardsMenuWidth: 240,
      },
    }),
    [mode, globalTheme],
  );

  return <StyledThemeProvider theme={defaultTheme}>{combinedProviders}</StyledThemeProvider>;
}
